"use client";

import { useMemo, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";
import { cn } from "@/modern/utils";

/** Wide enough for 6 advisor chips with single-line labels (e.g. Grace's Evaluations). */
const FLOATING_WIDTH = 820;
const FLOATING_MIN_WIDTH = 640;
const FLOATING_MAX_HEIGHT_PX = 820;
const FLOATING_HEIGHT_VH = 0.88;
const FLOATING_MIN_HEIGHT_PX = 480;

const getFloatingWidthPx = () => Math.min(FLOATING_WIDTH, window.innerWidth - 32);

const getFloatingHeightPx = () =>
  Math.min(window.innerHeight * FLOATING_HEIGHT_VH, FLOATING_MAX_HEIGHT_PX);

export const AnalyzeFloatingPanel = ({ children }: { children: ReactNode }) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const defaultPosition = useMemo(() => {
    const widthPx = getFloatingWidthPx();
    const heightPx = getFloatingHeightPx();
    return {
      x: Math.max(16, (window.innerWidth - widthPx) / 2),
      y: Math.max(16, (window.innerHeight - heightPx) / 2),
    };
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[calc(var(--swivu-bryntum-z-index)+24)] pointer-events-none">
      {/* Light scrim for depth; clicks pass through to the file modal behind */}
      <div
        className="absolute inset-0 bg-black/20 dark:bg-black/45 pointer-events-none"
        aria-hidden
      />
      <Draggable
        nodeRef={nodeRef}
        handle=".analyze-float-drag-handle"
        cancel="button, input, textarea, select, [role='combobox'], a"
        bounds="parent"
        defaultPosition={defaultPosition}
      >
        <div
          ref={nodeRef}
          className={cn(
            "pointer-events-auto absolute flex flex-col overflow-hidden rounded-xl resize",
            "bg-card text-card-foreground",
            "border-2 border-border/90",
            "ring-1 ring-black/10 dark:ring-white/12",
            "shadow-[0_28px_70px_-12px_rgba(0,0,0,0.55)] dark:shadow-[0_28px_70px_-12px_rgba(0,0,0,0.75)]",
          )}
          style={{
            width: getFloatingWidthPx(),
            minWidth: FLOATING_MIN_WIDTH,
            height: `min(${FLOATING_HEIGHT_VH * 100}vh, ${FLOATING_MAX_HEIGHT_PX}px)`,
            minHeight: FLOATING_MIN_HEIGHT_PX,
            maxHeight: "90vh",
          }}
        >
          {children}
        </div>
      </Draggable>
    </div>,
    document.body,
  );
};
